import { useToast } from '@adamdotai/design-system'
import {
    INSERT_ACTION_COMMAND,
    INSERT_AGENDA_COMMAND,
    INSERT_CONTENT_ITEM_COMMAND,
    INSERT_DECISION_COMMAND,
} from '@adamdotai/editor/ContentItemPlugin'
import { $createListNode, createListItem } from '@adamdotai/editor/List'
import {
    $createAdamHeadingNode,
    $createAdamParagraphNode,
    INSERT_ADAM_NOTE_COMMAND,
} from '@adamdotai/editor/NotesPlugin'
import type { LexicalEditor } from 'lexical'
import { $createTextNode, $getRoot } from 'lexical'
import { useTranslations } from 'next-intl'
import type { DeepPartial } from 'utility-types'
import type {
    MeetingSummary,
    RecommendedContentItem,
} from '@/generated/gql/graphql'
import { LinkEntityType } from '@/generated/gql/graphql'

type AddSummaryToEditorProps = {
    summary: DeepPartial<MeetingSummary>
}

type UseAddSummaryToEditorProps = {
    editor: LexicalEditor
}

const useAddSummaryToEditor = ({ editor }: UseAddSummaryToEditorProps) => {
    const t = useTranslations()
    const toast = useToast()

    const $clearEditor = () => {
        editor.update(
            () => {
                const root = $getRoot()
                root.clear()
                root.append($createAdamParagraphNode())
            },
            {
                discrete: true,
            },
        )
    }

    const $insertTitle = (title: string) => {
        editor.dispatchCommand(INSERT_ADAM_NOTE_COMMAND, {
            source: {
                insertStrategy: 'insert-after',
                nodeKey: $getRoot().getLastChild().getKey(),
                type: 'strategy-based',
            },
            createNote: () => {
                const node = $createAdamHeadingNode('h1')
                const text = $createTextNode(title)
                node.append(text)
                return node
            },
        })
    }

    const $insertList = (
        subItems: Array<DeepPartial<RecommendedContentItem>>,
    ) => {
        editor.dispatchCommand(INSERT_CONTENT_ITEM_COMMAND, {
            source: {
                insertStrategy: 'insert-after',
                nodeKey: $getRoot().getLastChild().getKey(),
                type: 'strategy-based',
            },
            createContentItem: () => {
                const list = $createListNode('bullet')
                list.append(
                    ...subItems.map((subItem) => {
                        const listItem = createListItem()
                        listItem
                            .getTextEditableNode()
                            .append($createTextNode(subItem.recommendedTitle))
                        return listItem
                    }),
                )
                return list
            },
        })
    }

    const $insertNote = (content = '') => {
        editor.dispatchCommand(INSERT_ADAM_NOTE_COMMAND, {
            source: {
                insertStrategy: 'insert-after',
                nodeKey: $getRoot().getLastChild().getKey(),
                type: 'strategy-based',
            },
            createNote: () => {
                const node = $createAdamParagraphNode()
                const text = $createTextNode(content)
                node.append(text)
                return node
            },
        })
    }

    const $insertAgendaItem = (agenda: DeepPartial<RecommendedContentItem>) => {
        if (agenda.subItems.length > 0) {
            $insertList(agenda.subItems)
        }
        editor.dispatchCommand(INSERT_AGENDA_COMMAND, {
            source: {
                insertStrategy: 'insert-after',
                nodeKey: $getRoot().getLastChild().getKey(),
                type: 'strategy-based',
            },
            payload: {
                name: agenda.recommendedTitle,
            },
        })
    }

    const $insertActionItem = (action: DeepPartial<RecommendedContentItem>) => {
        if (action.subItems.length > 0) {
            $insertList(action.subItems)
        }
        editor.dispatchCommand(INSERT_ACTION_COMMAND, {
            source: {
                insertStrategy: 'insert-after',
                nodeKey: $getRoot().getLastChild().getKey(),
                type: 'strategy-based',
            },
            payload: {
                title: action.recommendedTitle,
            },
        })
    }

    const $insertDecisionItem = (
        decision: DeepPartial<RecommendedContentItem>,
    ) => {
        if (decision.subItems.length > 0) {
            $insertList(decision.subItems)
        }
        editor.dispatchCommand(INSERT_DECISION_COMMAND, {
            source: {
                insertStrategy: 'insert-after',
                nodeKey: $getRoot().getLastChild().getKey(),
                type: 'strategy-based',
            },
            payload: {
                title: decision.recommendedTitle,
            },
        })
    }

    const addSummaryToEditor = ({ summary }: AddSummaryToEditorProps) => {
        editor.getEditorState().read(() => {
            $insertNote()

            const decisionItems = summary.recommendedContentItems.filter(
                (i) => i.contentType === LinkEntityType.Decision,
            )

            if (decisionItems.length > 0) {
                decisionItems.reverse().forEach($insertDecisionItem)
                $insertTitle(t('glossary.decisions'))
            }

            const actionItems = summary.recommendedContentItems.filter(
                (i) => i.contentType === LinkEntityType.Action,
            )

            if (actionItems.length > 0) {
                actionItems.reverse().forEach($insertActionItem)
                $insertTitle(t('glossary.action-items'))
            }

            const agendaItems = summary.recommendedContentItems.filter(
                (i) => i.contentType === LinkEntityType.Agenda,
            )

            if (agendaItems.length > 0) {
                agendaItems.reverse().forEach($insertAgendaItem)
                $insertTitle(t('glossary.agenda'))
            }

            const [overviewNote] = summary.recommendedContentItems.filter(
                (i) => i.contentType === LinkEntityType.Note,
            )

            if (overviewNote != null) {
                $insertNote(overviewNote.recommendedTitle)
                $insertTitle(t('common.overview'))
            }

            $insertNote()
        })
        toast({
            status: 'success',
            content: t('toast.summary-added-to-notes-successfully'),
        })
    }

    const replaceContentWithSummary = (props: AddSummaryToEditorProps) => {
        $clearEditor()
        addSummaryToEditor(props)
    }

    return { addSummaryToEditor, replaceContentWithSummary }
}

export { useAddSummaryToEditor }
